import { NavLink, useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { useTheme } from '../context/ThemeContext'
import {
  IconLayoutDashboard, IconUsers, IconAddressBook, IconBriefcase,
  IconCalendarEvent, IconCalendar, IconChartBar, IconToolsKitchen2,
  IconFileText, IconFileCertificate, IconUsersGroup, IconUser,
  IconLogout, IconMoon, IconSun, IconBuildingSkyscraper,
  IconSettings, IconComponents, IconShieldCog, IconColumns,
  IconClipboardList, IconReceipt, IconTrendingUp, IconBed
} from '@tabler/icons-react'

const SECTIONS = [
  {
    label: null,
    items: [{ path: '/dashboard', label: 'Dashboard', icon: IconLayoutDashboard, access: 'dashboard' }]
  },
  {
    label: 'Sales',
    items: [
      { path: '/leads', label: 'Leads', icon: IconUsers, access: 'leads' },
      { path: '/contacts', label: 'Contacts', icon: IconAddressBook, access: 'contacts' },
      { path: '/deals', label: 'Deals', icon: IconBriefcase, access: 'deals' },
    ]
  },
  {
    label: 'Events',
    items: [
      { path: '/events', label: 'Events & Groups', icon: IconCalendarEvent, access: 'events' },
      { path: '/beos', label: 'BEOs', icon: IconClipboardList, access: 'events' },
      { path: '/function-diary', label: 'Function Diary', icon: IconColumns, access: 'function_diary' },
      { path: '/catering', label: 'Catering & Menus', icon: IconToolsKitchen2, access: 'catering' },
      { path: '/calendar', label: 'Calendar', icon: IconCalendar, access: 'calendar' },
    ]
  },
  {
    label: 'Documents',
    items: [
      { path: '/proposals', label: 'Proposals', icon: IconFileText, access: 'proposals' },
      { path: '/contracts', label: 'Contracts', icon: IconFileCertificate, access: 'contracts' },
      { path: '/invoices', label: 'Invoices', icon: IconReceipt, access: 'deals' },
      { path: '/group-resume', label: 'Group Resume', icon: IconBuildingSkyscraper, access: 'group_resume' },
    ]
  },
  {
    label: 'Tools',
    items: [
      { path: '/reports', label: 'Reports', icon: IconChartBar, access: 'reports' },
      { path: '/pace', label: 'Pace Report', icon: IconTrendingUp, access: 'reports' },
      { path: '/availability', label: 'Availability', icon: IconBed, access: 'reports' },
      { path: '/space', label: 'Space Designer', icon: IconComponents, access: 'space' },
    ]
  },
  {
    label: 'Admin',
    items: [
      { path: '/team', label: 'Team', icon: IconUsersGroup, access: 'team' },
      { path: '/settings', label: 'Settings', icon: IconSettings, access: 'settings' },
      { path: '/profile', label: 'My Profile', icon: IconUser, access: 'profile' },
    ]
  }
]

export default function Sidebar() {
  const { user, logout, hasAccess, isPlatformAdmin } = useAuth()
  const { dark, toggle } = useTheme()
  const navigate = useNavigate()

  const handleLogout = async () => { await logout(); navigate('/login') }
  const initials = user?.name?.split(' ').map(n => n[0]).join('').toUpperCase() || '?'

  return (
    <aside className="navbar navbar-vertical navbar-expand-lg" data-bs-theme="dark">
      <div className="container-fluid">
        <h1 className="navbar-brand navbar-brand-autodark">
          <a href="/" style={{ textDecoration: 'none', color: 'inherit' }}>
            <span className="d-flex align-items-center gap-2">
              <IconBuildingSkyscraper size={28} />
              <span><strong>Leadiox</strong><small className="d-block text-secondary" style={{ fontSize: '0.7rem', marginTop: -2 }}>Hotel Sales CRM</small></span>
            </span>
          </a>
        </h1>

        <div className="px-3 mb-2">
          <button className="btn btn-dark w-100 btn-sm d-flex align-items-center gap-2" onClick={toggle}>
            {dark ? <IconSun size={16} /> : <IconMoon size={16} />}
            {dark ? 'Light Mode' : 'Dark Mode'}
          </button>
        </div>

        <div className="collapse navbar-collapse" id="sidebar-menu">
          <ul className="navbar-nav pt-lg-3">
            {SECTIONS.map((section, si) => {
              const visible = section.items.filter(item => hasAccess(item.access))
              if (!visible.length) return null
              return (
                <li key={si} className="nav-item">
                  {section.label && <div className="nav-link-header" style={{ fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#6c7a89', padding: '0.5rem 0.75rem 0.15rem', marginTop: si > 1 ? '0.25rem' : 0 }}>{section.label}</div>}
                  {visible.map(item => (
                    <NavLink key={item.path} to={item.path} end={item.path === '/'} className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}>
                      <span className="nav-link-icon"><item.icon size={18} /></span>
                      <span className="nav-link-title">{item.label}</span>
                    </NavLink>
                  ))}
                </li>
              )
            })}
          </ul>
        </div>

        {isPlatformAdmin() && (
          <div className="px-3 mt-3 pt-3 border-top border-secondary">
            <NavLink to="/admin" className="btn w-100 btn-sm d-flex align-items-center gap-2" style={{ background: 'rgba(245,158,11,0.15)', color: '#f59e0b' }}>
              <IconShieldCog size={16} />Platform Admin
            </NavLink>
          </div>
        )}

        <div className="mt-auto px-3 py-3 border-top border-secondary">
          <div className="d-flex align-items-center gap-2">
            <span className="avatar avatar-sm rounded-circle bg-primary">{initials}</span>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div className="text-white fw-medium" style={{ fontSize: '0.85rem', lineHeight: 1.2 }}>{user?.name || 'User'}</div>
              <div className="text-secondary" style={{ fontSize: '0.7rem', textTransform: 'capitalize' }}>{user?.role?.replace(/_/g, ' ')}</div>
            </div>
            <button className="btn btn-ghost-secondary btn-sm btn-icon" onClick={handleLogout} title="Sign Out"><IconLogout size={18} /></button>
          </div>
        </div>
      </div>
    </aside>
  )
}
